/**
 * Camera selection identity.
 *
 * `settingsStore.selectedCamera` / `selectedSecondaryCamera` historically overload one
 * string with three meanings:
 *   - a webcam `deviceId` (opaque hash, unique per physical device — even identical models)
 *   - a USB/DSLR `productId` in decimal ("1234") — AMBIGUOUS between two same-model bodies
 *   - the literal 'detect' (auto-pick any PTP camera)
 *
 * To support two cameras of the same series we add a fourth, structured form that carries
 * the USB serial number:
 *
 *   usb:<vendorId>:<productId>:<serialNumber>
 *
 * WebUSB's USBDeviceFilter natively supports `serialNumber`, so passing the parsed params
 * to `navigator.usb.requestDevice` binds the request to ONE physical body even when its
 * twin is plugged in — the chooser list the main process receives is already narrowed to
 * the matching device, which keeps this compatible with older packaged main processes.
 *
 * All legacy forms keep working; `resolveConnectArgs` is the single parser every
 * connect/recovery path should use.
 */

import type { CameraDevice, DeviceType } from '@renderer/types/utils'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'

/**
 * Master gate for ALL dual-camera / same-series-identity behavior. Registered in the
 * Studio flag registry (config/featureFlags.ts, surface "app", default OFF) and
 * targeted per account via the superadmin email allowlist.
 *
 * OFF ⇒ byte-for-byte legacy behavior everywhere this module and uniCamUtils touch:
 * legacy bare-productId selection values, no serial filters, close-all USB teardown,
 * unsuffixed webcam labels, no secondary boot connect, single-pane kotak capture.
 * PARSING of already-persisted `usb:` values stays on unconditionally — flipping the
 * flag off must never strand a booth with an unreadable saved selection.
 */
export const DUAL_CAMERA_FLAG = 'dual_camera'

export function isDualCameraEnabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(DUAL_CAMERA_FLAG, false)
}

/**
 * Native gphoto2 sidecar backend (gphoto2-daemon child process in the main
 * process) instead of the renderer's WASM web-gphoto2. Registered in the Studio
 * flag registry (config/featureFlags.ts, surface "app"), default OFF.
 *
 * The flag alone is NOT sufficient to switch backends — the packaged main must
 * carry the sidecar IPC handlers AND the daemon binary must be installed. Gate
 * with `isNativeCameraAvailable()` (lib/native-gphoto2) alongside this, so a
 * flagged booth on an older main or without the helper silently stays on WASM.
 */
export const NATIVE_GPHOTO2_FLAG = 'native_gphoto2'

/**
 * Second WASM camlib build (lib/web-gphoto2-v2): libgphoto2 2.5.31.1 emscripten fork
 * rebuilt on emsdk 3.1.73 — 500ms autofocus ceiling inside the capture instead of the
 * v1 build's 2000ms, EOS R100/R50 in the model table, and where the binding is present
 * a shutter press without autofocus. Registered in the Studio flag registry, default
 * OFF. Read once at Camera construction; a flip takes effect on the next connect.
 */
export const WASM_GPHOTO2_V2_FLAG = 'wasm_gphoto2_v2'

export function isWasmV2Enabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(WASM_GPHOTO2_V2_FLAG, false)
}

export const CANON_USB_VENDOR_ID = 0x04a9

export type WasmVariant = 'v1' | 'v2'

/**
 * Which WASM camlib build to bind a body to, decided per BODY once its USB vendor is
 * known (from the connected USBDevice — never from the persisted selection string,
 * which is a bare productId on most booths).
 *
 * Everything v2 buys is Canon-specific: the `eosremoterelease` no-AF press and the EOS
 * autofocus ceiling. A Sony, Nikon or Fuji body gains nothing from v2 — and the bodies
 * that v2 hurt in the field (2026-09-15) were exactly the non-Canon ones, where v2's
 * Canon-widget probes failed. So with the flag on, only Canon gets v2; every other vendor
 * stays on v1 exactly as if the flag were off.
 */
export function pickWasmVariant(
  vendorId: number | undefined | null,
  flagOn: boolean = isWasmV2Enabled()
): WasmVariant {
  if (!flagOn) return 'v1'
  return vendorId === CANON_USB_VENDOR_ID ? 'v2' : 'v1'
}

export function isNativeGphoto2Enabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(NATIVE_GPHOTO2_FLAG, false)
}

/**
 * Camera Reconnect v2 (Phase 2 of docs/camera-rest-plan.md): before showing any
 * chooser, connect paths scan `navigator.usb.getDevices()` for a still-granted
 * device matching the saved selection and bind web-gphoto2 to it directly —
 * skipping `requestDevice()` and its user-gesture requirement. Grants persist in
 * Electron's session memory until app restart or physical unplug, so this makes
 * every SOFT reconnect (recovery mid-session, wake after a legacy idle-eject,
 * WASM-slot rebuild) gesture-free on ALL packaged main versions. OFF ⇒ every
 * connect goes through the legacy requestDevice path.
 */
export const CAMERA_RECONNECT_V2_FLAG = 'camera_reconnect_v2'

export function isReconnectV2Enabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(CAMERA_RECONNECT_V2_FLAG, false)
}

export interface UsbSelectionParams {
  vendorId?: number
  productId?: number
  serialNumber?: string
  /**
   * WHICH of several identical, serial-less bodies the operator picked in the
   * dropdown (1-based). NOT identity and never a USB filter — a Canon R100
   * exposes no iSerialNumber at all, so nothing stable distinguishes twins and
   * the physical body a request lands on is decided by the main process's
   * per-cameraKey deviceId exclusion. Its only job is to make the two dropdown
   * entries carry DIFFERENT values: two `<option>`s sharing one value make the
   * `<select>` render the first for both slots, which is why the Bahela Kotak
   * booth showed "Canon Device #1" on the secondary tab too.
   */
  slotOrdinal?: number
}

export interface ResolvedConnectArgs {
  deviceType: DeviceType
  params: string | UsbSelectionParams
}

const USB_SELECTION_PREFIX = 'usb:'

/**
 * Marks the tail of a selection value as a SLOT ORDINAL rather than a serial:
 * `usb:1193:13074:#2`. A real iSerialNumber never starts with '#', so this stays
 * unambiguous against every serial we have seen in the fleet, and old values
 * (`usb:v:p:SN`, `usb:v:p:`, bare `13074`) parse exactly as before.
 */
const SLOT_ORDINAL_MARKER = '#'

export function makeUsbSelectionValue(
  vendorId: number,
  productId: number,
  serialNumber?: string,
  slotOrdinal?: number
): string {
  const tail = serialNumber
    ? serialNumber
    : slotOrdinal && slotOrdinal > 1
      ? `${SLOT_ORDINAL_MARKER}${slotOrdinal}`
      : ''
  return `${USB_SELECTION_PREFIX}${vendorId}:${productId}:${tail}`
}

export function isUsbSelectionValue(selected: string | undefined): boolean {
  return typeof selected === 'string' && selected.startsWith(USB_SELECTION_PREFIX)
}

export function parseUsbSelectionValue(selected: string): UsbSelectionParams | undefined {
  if (!isUsbSelectionValue(selected)) return undefined
  const [vendorRaw, productRaw, ...serialParts] = selected
    .slice(USB_SELECTION_PREFIX.length)
    .split(':')
  const vendorId = Number(vendorRaw)
  const productId = Number(productRaw)
  if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) return undefined
  // Serial numbers may themselves contain ':' — rejoin the tail.
  const tail = serialParts.join(':')
  if (tail.startsWith(SLOT_ORDINAL_MARKER)) {
    // Slot ordinal, NOT a serial. Must never reach a USBDeviceFilter: filtering on
    // serialNumber: '#2' matches no device and the camera would simply never connect.
    const slotOrdinal = Number(tail.slice(SLOT_ORDINAL_MARKER.length))
    return {
      vendorId,
      productId,
      slotOrdinal: Number.isFinite(slotOrdinal) ? slotOrdinal : undefined
    }
  }
  return {
    vendorId,
    productId,
    serialNumber: tail !== '' ? tail : undefined
  }
}

/**
 * Map a stored camera selection value to `unifiedCameraUtils.tryToConnect` arguments.
 * Handles every historical format; unknown strings are treated as webcam deviceIds
 * (the pre-existing fallback behavior).
 */
export function resolveConnectArgs(selected: string): ResolvedConnectArgs {
  if (selected === 'detect') {
    return { deviceType: 'camera', params: {} }
  }
  const usbParams = parseUsbSelectionValue(selected)
  if (usbParams) {
    return { deviceType: 'camera', params: usbParams }
  }
  if (selected !== '' && !isNaN(Number(selected))) {
    // Legacy bare productId (cannot distinguish same-model twins).
    return { deviceType: 'camera', params: { productId: Number(selected) } }
  }
  return { deviceType: 'webcam', params: selected }
}

/**
 * Build the selection value for an enumerated USB camera: serial-qualified when the
 * main process exposed a serial (new builds), slot-qualified when it did not but the
 * body is one of several identical ones, legacy bare productId otherwise.
 *
 * `slotOrdinal` comes from {@link disambiguateUsbCameraLabels} and exists only so the
 * Nth duplicate gets a value distinct from the 1st. Without it both entries of a
 * same-model pair render as one option and the operator cannot assign the second body
 * to the secondary slot at all — they pick "#2" and the control snaps back to "#1".
 */
export function makeUsbSelectionValueForDevice(cam: CameraDevice, slotOrdinal?: number): string {
  if (isDualCameraEnabled() && cam.vendorId !== undefined && cam.productId !== undefined) {
    if (cam.serialNumber) {
      return makeUsbSelectionValue(cam.vendorId, cam.productId, cam.serialNumber)
    }
    if (slotOrdinal && slotOrdinal > 1) {
      return makeUsbSelectionValue(cam.vendorId, cam.productId, undefined, slotOrdinal)
    }
  }
  return `${cam.productId}`
}

/**
 * Display suffix for a USB camera — empty when dual_camera is off. Serial when the
 * body exposes one; USB bus.address otherwise (e.g. Canon R100 has NO iSerialNumber),
 * so two same-model bodies at least render as visibly distinct dropdown entries.
 * Bus.address is per-plug-session (changes on replug) — display only, never identity.
 */
export function usbCameraSerialSuffix(cam: CameraDevice): string {
  if (!isDualCameraEnabled()) return ''
  if (cam.serialNumber) return ` · SN ${cam.serialNumber}`
  if (cam.busNumber !== undefined && cam.deviceAddress !== undefined) {
    return ` · USB ${cam.busNumber}.${cam.deviceAddress}`
  }
  return ''
}

/**
 * Two same-model USB bodies with no serial AND the same USB bus.address (or when the
 * suffix is otherwise identical) render byte-identical in the dropdown, so the operator
 * can't tell which entry is which even though they're physically different cameras. Add a
 * stable ` #n` ordinal to each member of a duplicate group. Returns per-camera the extra
 * suffix to append AFTER `usbCameraSerialSuffix` (empty when the label is already unique)
 * and the matching `ordinal`, which callers MUST pass to
 * {@link makeUsbSelectionValueForDevice} so the option values differ too — a label that
 * says "#2" over a value identical to "#1" is worse than no ordinal at all.
 */
export function disambiguateUsbCameraLabels(
  usbCameras: CameraDevice[]
): { camera: CameraDevice; idx: number; suffix: string; ordinal?: number }[] {
  const baseLabel = (c: CameraDevice): string => `${c.brand}${usbCameraSerialSuffix(c)}`
  const counts = new Map<string, number>()
  for (const c of usbCameras) counts.set(baseLabel(c), (counts.get(baseLabel(c)) || 0) + 1)
  const ordinal = new Map<string, number>()
  return usbCameras.map((camera, idx) => {
    const base = baseLabel(camera)
    if ((counts.get(base) || 0) <= 1) return { camera, idx, suffix: '' }
    const n = (ordinal.get(base) || 0) + 1
    ordinal.set(base, n)
    return { camera, idx, suffix: ` #${n}`, ordinal: n }
  })
}

/**
 * Like {@link findUsbCameraBySelection}, but a serial-qualified selection that finds
 * no exact match falls back to vendorId+productId. At cold boot node-usb often cannot
 * read serials on Windows (driver/claim limits) and the Chromium session backfill is
 * empty until the first chooser round — so an exact-serial lookup wrongly reports the
 * still-attached camera as "not found" and the booth never offers the reconnect
 * prompt. Use this for PRESENCE checks (should we prompt?); keep the exact variant
 * for identity-critical paths (dual-camera twin selection).
 */
export function findUsbCameraBySelectionLoose(
  usbCameras: CameraDevice[] | undefined,
  selected: string
): CameraDevice | undefined {
  const exact = findUsbCameraBySelection(usbCameras, selected)
  if (exact) return exact
  const structured = parseUsbSelectionValue(selected)
  if (structured?.serialNumber && usbCameras) {
    return usbCameras.find(
      (c) => c.vendorId === structured.vendorId && c.productId === structured.productId
    )
  }
  return undefined
}

/** Find the enumerated USB camera a stored selection value refers to (any format). */
export function findUsbCameraBySelection(
  usbCameras: CameraDevice[] | undefined,
  selected: string
): CameraDevice | undefined {
  if (!usbCameras || selected === 'detect') return undefined
  const structured = parseUsbSelectionValue(selected)
  if (structured) {
    const sameModel = usbCameras.filter(
      (c) =>
        c.vendorId === structured.vendorId &&
        c.productId === structured.productId &&
        (!structured.serialNumber || c.serialNumber === structured.serialNumber)
    )
    if (structured.slotOrdinal && structured.slotOrdinal > 1) {
      // Nth identical body, in the same enumeration order the dropdown listed them.
      // Falls back to the first when only one is currently attached, so unplugging
      // the twin degrades to "found" rather than "camera missing".
      return sameModel[structured.slotOrdinal - 1] ?? sameModel[0]
    }
    return sameModel[0]
  }
  if (selected !== '' && !isNaN(Number(selected))) {
    return usbCameras.find((c) => `${c.productId}` === selected)
  }
  return undefined
}

/**
 * Two identical-model webcams report byte-identical labels ("HD Webcam (046d:0825)")
 * even though their deviceIds are distinct. Suffix duplicates with a stable ordinal so
 * the operator can tell them apart in dropdowns; order is stabilized by deviceId so the
 * suffix doesn't shuffle between renders/reboots (it CAN change if a camera moves USB
 * port — deviceId is derived from the OS device path).
 */
export function disambiguateWebcamLabels(
  webcams: MediaDeviceInfo[]
): { deviceId: string; label: string }[] {
  if (!isDualCameraEnabled()) {
    // Flag off: identical rendering to the pre-dual-camera dropdowns.
    return webcams.map((cam) => ({ deviceId: cam.deviceId, label: cam.label || '' }))
  }
  const labelCounts = new Map<string, number>()
  for (const cam of webcams) {
    const label = cam.label || ''
    labelCounts.set(label, (labelCounts.get(label) || 0) + 1)
  }

  const ordinal = new Map<string, number>()
  return [...webcams]
    .sort((a, b) => a.deviceId.localeCompare(b.deviceId))
    .map((cam) => {
      const base = cam.label || ''
      if ((labelCounts.get(base) || 0) <= 1) {
        return { deviceId: cam.deviceId, label: base }
      }
      const n = (ordinal.get(base) || 0) + 1
      ordinal.set(base, n)
      return { deviceId: cam.deviceId, label: `${base} #${n}` }
    })
}
